import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names conditionally and merges Tailwind conflicts safely.
 * Example: cn("p-2", isActive && "bg-primary")
 */
export function cn(...inputs: any[]) {
  return twMerge(clsx(inputs));
}

/**
 * Values safe for next/image: absolute http(s), root-relative /..., blob:, data:.
 * Rejects placeholders such as the literal "string" from OpenAPI examples.
 */
export function isSafeImageSrc(src: string | undefined | null): src is string {
  if (src == null) return false;
  const s = src.trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (
    lower === "string" ||
    lower === "null" ||
    lower === "undefined" ||
    lower === "none"
  ) {
    return false;
  }
  if (s.startsWith("blob:") || s.startsWith("data:")) return true;
  if (s.startsWith("/")) return true;
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      new URL(s);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Values safe for navigation sinks (anchor.href, fetch, window.open):
 * absolute http(s), root-relative /..., and blob:.
 * Intentionally excludes data: to prevent URL-based script/content injection sinks.
 */
export function isSafeNavigationSrc(
  src: string | undefined | null,
): src is string {
  if (src == null) return false;
  const s = src.trim();
  if (!s) return false;
  const lower = s.toLowerCase();

  if (
    lower === "string" ||
    lower === "null" ||
    lower === "undefined" ||
    lower === "none"
  ) {
    return false;
  }

  if (s.startsWith("blob:")) return true;
  if (s.startsWith("/")) return true;
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      new URL(s);
      return true;
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Normalize a validated URL before assigning to anchor.href, fetch(), or window.open().
 * Rejects javascript:, data:, and other unsafe schemes via isSafeNavigationSrc.
 */
export function toSafeAnchorHref(
  src: string,
  baseOrigin = "http://localhost",
): string {
  if (!isSafeNavigationSrc(src)) {
    throw new Error("Unsafe URL");
  }

  const trimmed = src.trim();
  if (trimmed.startsWith("blob:")) {
    return trimmed;
  }
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return new URL(trimmed).href;
  }

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : baseOrigin;
  return new URL(trimmed, origin).href;
}

export type ImageSrcLike =
  | string
  | { url?: string | null; src?: string | null }
  | null
  | undefined;

/** Pull a URL string from API shapes (`string`, `{ url }`, `{ src }`). */
export function extractImageUrl(value: ImageSrcLike): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "object") {
    if (typeof value.url === "string" && value.url.trim()) {
      return value.url.trim();
    }
    if (typeof value.src === "string" && value.src.trim()) {
      return value.src.trim();
    }
  }
  return undefined;
}

function apiBaseUrl(): string {
  return (process.env.NEXT_PUBLIC_BASE_URL ?? "").replace(/\/$/, "");
}

/**
 * Turn stored upload paths into absolute API URLs and leave static/blob/data URLs as-is.
 */
export function resolveImageUrl(
  src: string | undefined | null,
): string | undefined {
  const raw = typeof src === "string" ? src.trim() : extractImageUrl(src);
  if (!raw) return undefined;

  if (
    raw.startsWith("blob:") ||
    raw.startsWith("data:") ||
    raw.startsWith("http://") ||
    raw.startsWith("https://")
  ) {
    return raw;
  }

  const base = apiBaseUrl();

  if (raw.startsWith("/upload/")) {
    return base ? `${base}${raw}` : raw;
  }

  if (raw.startsWith("upload/")) {
    return base ? `${base}/${raw}` : `/${raw}`;
  }

  return raw;
}

export function normalizeImageUrls(
  values: ImageSrcLike[] | null | undefined,
): string[] {
  if (!values?.length) return [];
  return values
    .map((item) => resolveImageUrl(extractImageUrl(item)))
    .filter((url): url is string => Boolean(url) && isSafeImageSrc(url));
}

/** Skip Next.js optimizer for local blobs and API-served uploads. */
export function shouldUnoptimizeImageSrc(src: string): boolean {
  if (src.startsWith("blob:") || src.startsWith("data:")) return true;
  const base = apiBaseUrl();
  if (base && src.startsWith(`${base}/upload/`)) return true;
  return /\/upload\/(image|file)\//.test(src);
}


